import Button, { ButtonVariant } from '@components/buttons/button'
import {
  type SanityFilter,
  type SanityFilterGroup,
  type SanitySort,
} from '@data/sanity/queries/types/cart'
import { type UpdateUrlParameters, type UrlParameter } from '@lib/hooks'
import { type Filter } from '@lib/product'
import { getSortParameterValue } from '@lib/product-collection'
import { StringsContext } from '@lib/strings-context'
import cx from 'classnames'
import { useContext } from 'react'
import CollectionFilter from './filter'
import CollectionSort from './sort'

interface CollectionFilterAndSortProps {
  filter?: SanityFilter
  sort?: SanitySort
  processedProductCount: number
  paginatedProcessedProductCount: number
  activeFilters: Filter[]
  filterGroups: SanityFilterGroup[]
  activeParameters: UrlParameter[]
  updateParameters: UpdateUrlParameters
}

const CollectionFilterAndSort = ({
  filter,
  sort,
  processedProductCount,
  paginatedProcessedProductCount,
  filterGroups,
  activeFilters,
  activeParameters,
  updateParameters,
}: CollectionFilterAndSortProps) => {
  const strings = useContext(StringsContext)

  const isFilterActive = Boolean(filter?.isActive)
  const isSortActive = Boolean(sort?.isActive)

  const activeSortValue = getSortParameterValue(activeParameters)
  const activeFilterValueCount = activeFilters.reduce((total, activeFilter) => {
    return total + activeFilter.values.length
  }, 0)

  const clearFilters = () => {
    updateParameters(
      activeFilters.map((activeFilter: Filter) => ({
        name: activeFilter.name,
        value: null,
      })),
    )
  }

  return (
    <div className="container flex flex-col sm:flex-row sm:items-center border-b border-gray bg-pageBG bg-opacity-95">
      {/* Filter controls */}
      {isFilterActive && (
        <div className="flex items-center py-3.5 w-full sm:w-auto">
          <CollectionFilter
            filterGroups={filterGroups}
            activeFilters={activeFilters}
            activeFilterValueCount={activeFilterValueCount}
            itemTotal={processedProductCount}
            updateParameters={updateParameters}
          />
          {activeFilterValueCount > 0 && (
            <Button
              variant={ButtonVariant.LINK}
              onClick={clearFilters}
              className="uppercase ml-auto sm:ml-4"
            >
              {strings.collectionClearAllLabel}
            </Button>
          )}
        </div>
      )}

      {/* Filler  */}
      <div className="grow" />

      {/* Product count and sort controls */}
      <div
        className={cx('flex items-center border-gray w-full sm:w-auto', {
          'border-t sm:border-t-0 py-3.5 sm:py-0': isFilterActive,
          'py-3.5': !isFilterActive,
        })}
      >
        {processedProductCount > 0 && (
          <span
            aria-live="polite"
            role="status"
            aria-atomic="true"
            className="text-xs text-gray-dark leading-none"
          >
            {strings.collectionProductCount
              ?.replace(/{count}/gi, `${paginatedProcessedProductCount}`)
              ?.replace(/{total}/gi, `${processedProductCount}`)}
          </span>
        )}

        {/* Filler  */}
        <div className="grow" />

        {/* Sort controls */}
        {isSortActive && sort && (
          <div className="ml-auto sm:ml-3">
            <CollectionSort
              sortOptions={sort.options ?? []}
              activeSortValue={activeSortValue}
              updateParameters={updateParameters}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default CollectionFilterAndSort
